import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useGetApi } from "../../../hooks/useGetApi";
import {
  addCategoryApi,
  getCategoryApi,
  updateCategoryApi,
} from "../../../services/category.service";
import { toast } from "react-toastify";
import SubCategorySchema from "../../../joi/master/sub-category.schema";

export default function SubCategoryModal({
  open,
  onClose,
  selectedRow,
  refetch,
}) {
  const initialState = {
    parentCategory: null,
    categoryName: "",
    code: "",
    isClaimRateAllowed: false,
    isSaleRate: false,
    saleRateBasedOn: {
      option: "",
      percentage: 0,
    },
    limitSaleRateToMaximumToMRP: false,
    limitSaleRateToMinimumToPurchaseRate: false,
    limitSaleRateBetweenMRPAndPurchaseRate: false,
    applyCondition: false,
    active: true,
    allowDecimalInQty: false,
    isProviderMandatory: false,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevValue) => {
      // Handle nested saleRateBasedOn fields
      if (name === "option" || name === "percentage") {
        return {
          ...prevValue,
          saleRateBasedOn: {
            ...prevValue.saleRateBasedOn,
            [name]: type === "number" ? parseFloat(value) || 0 : value,
          },
        };
      }

      return {
        ...prevValue,
        [name]: type === "checkbox" ? checked : value,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    const { error } = SubCategorySchema.validate({
      ...formData,
      parentCategory: formData?.parentCategory?._id,
    });

    if (error) {
      error.details.forEach((err) => toast.error(err.message));
      return;
    }

    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateCategoryApi({
        ...formData,
        parentCategory: formData?.parentCategory?._id,
      });
    } else {
      response = await addCategoryApi({
        ...formData,
        parentCategory: formData?.parentCategory?._id,
      });
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `Sub Category ${selectedRow?._id ? "updated" : "added"} successfully`
      );
      setFormData(initialState);
      onClose();
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const { dataList: categoryList } = useGetApi({
    apiFunction: getCategoryApi,
    params: {
      limit: Number.MAX_SAFE_INTEGER,
      type: "category",
    },
    dataKey: "categories",
    countKey: "total",
  });

  // Sync selectedRow data when modal opens
  useEffect(() => {
    if (selectedRow?._id) {
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        parentCategory: selectedRow?.parentCategory || null,
        categoryName: selectedRow?.categoryName || "",
        code: selectedRow?.code || "",
        isClaimRateAllowed: selectedRow?.isClaimRateAllowed ?? false,
        isSaleRate: selectedRow?.isSaleRate ?? false,
        saleRateBasedOn: {
          option: selectedRow?.saleRateBasedOn?.option || "",
          percentage: selectedRow?.saleRateBasedOn?.percentage ?? 0,
        },
        limitSaleRateToMaximumToMRP:
          selectedRow?.limitSaleRateToMaximumToMRP ?? false,
        limitSaleRateToMinimumToPurchaseRate:
          selectedRow?.limitSaleRateToMinimumToPurchaseRate ?? false,
        limitSaleRateBetweenMRPAndPurchaseRate:
          selectedRow?.limitSaleRateBetweenMRPAndPurchaseRate ?? false,
        applyCondition: selectedRow?.applyCondition ?? false,
        active: selectedRow?.active ?? true,
        allowDecimalInQty: selectedRow?.allowDecimalInQty ?? false,
        isProviderMandatory: selectedRow?.isProviderMandatory ?? false,
      });
    } else {
      setFormData(initialState);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            maxWidth: "unset",
            width: { xs: "95vw", lg: "1100px" },
            bgcolor: "#E5F4FF",
            borderRadius: "20px",
          },
        },
      }}
    >
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: "20px",
          }}
        >
          <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
            {selectedRow?._id ? `Edit Sub Category` : `Add Sub Category`}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                options={categoryList || []}
                getOptionLabel={(option) => option?.categoryName || ""}
                name="parentCategory"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category Name"
                    required
                    fullWidth
                  />
                )}
                value={formData?.parentCategory || ""}
                onChange={(_, newValue) =>
                  handleChange({
                    target: { name: "parentCategory", value: newValue },
                  })
                }
                sx={{ bgcolor: "white" }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Sub Category Name"
                name="categoryName"
                required
                fullWidth
                value={formData?.categoryName || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Code"
                name="code"
                required
                fullWidth
                value={formData?.code || ""}
                onChange={handleChange}
                sx={{ bgcolor: "white" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isProviderMandatory"
                    checked={formData?.isProviderMandatory}
                    onChange={handleChange}
                  />
                }
                label="Is Provider Mandatory"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData?.active}
                    onChange={handleChange}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  border: `1px solid rgba(204,212,218,1)`,
                  borderRadius: "12px",
                  py: 1,
                  px: 2,
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isSaleRate"
                          checked={formData?.isSaleRate}
                          onChange={handleChange}
                        />
                      }
                      label="Is Sale Rate?"
                    />
                  </Grid>
                  {formData?.isSaleRate && (
                    <>
                      <Grid item xs={12}>
                        <Typography>Sale Rate Based On*</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Option"
                          name="option"
                          required
                          fullWidth
                          value={formData?.saleRateBasedOn?.option || ""}
                          onChange={handleChange}
                          sx={{ bgcolor: "white" }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Percentage"
                          name="percentage"
                          type="number"
                          required
                          fullWidth
                          value={formData?.saleRateBasedOn?.percentage || ""}
                          onChange={handleChange}
                          sx={{ bgcolor: "white" }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">%</InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="limitSaleRateToMaximumToMRP"
                          checked={formData?.limitSaleRateToMaximumToMRP}
                          onChange={handleChange}
                        />
                      }
                      label="Limit Sale Rate To Maximum To MRP"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="limitSaleRateToMinimumToPurchaseRate"
                          checked={
                            formData?.limitSaleRateToMinimumToPurchaseRate
                          }
                          onChange={handleChange}
                        />
                      }
                      label="Limit Sale Rate To Minimum To Purchase Rate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="limitSaleRateBetweenMRPAndPurchaseRate"
                          checked={
                            formData?.limitSaleRateBetweenMRPAndPurchaseRate
                          }
                          onChange={handleChange}
                        />
                      }
                      label="Limit Sale Rate Between MRP And Purchase Rate"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="applyCondition"
                          checked={formData?.applyCondition}
                          onChange={handleChange}
                        />
                      }
                      label="Apply Condition"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} />
              ) : selectedRow?._id ? (
                "Update"
              ) : (
                `Submit`
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
